import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Target, Lightbulb, Users, Award, CheckCircle2, ArrowRight,
  Shield, Zap, BarChart3, Building2
} from 'lucide-react';
import { CTABanner } from '../components/shared/CTABanner';
import { SectionHeader } from '../components/shared/SectionHeader';

const TEAM_IMAGE = "https://images.pexels.com/photos/7794060/pexels-photo-7794060.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const philosophy = [
  {
    icon: Shield,
    title: 'Engineering First',
    description: 'Utility-grade system design that meets strict technical and regulatory standards.'
  },
  {
    icon: Lightbulb,
    title: 'Innovation Always',
    description: 'Smart charging technology, advanced analytics, and adaptive energy management.'
  },
  {
    icon: BarChart3,
    title: 'Reliability at Scale',
    description: 'Infrastructure built to grow with demand and maintain performance.'
  },
  {
    icon: Users,
    title: 'Partnership Focused',
    description: 'Long-term operational support and turnkey solutions for every client.'
  }
];

const stats = [
  { value: '500+', label: 'Sites Deployed' },
  { value: '15+', label: 'Years Experience' },
  { value: '50', label: 'States Covered' },
  { value: '99.5%', label: 'Avg. Uptime' }
];

const milestones = [
  { year: '2010', event: 'Skytec founded as a telecommunications infrastructure company' },
  { year: '2015', event: 'Expanded into utility-scale power systems' },
  { year: '2019', event: 'Launched EV charging division' },
  { year: '2021', event: 'Skytec EV established as dedicated EV infrastructure company' },
  { year: '2023', event: 'Reached 500+ site deployments nationwide' },
  { year: '2024', event: 'Launched Charging-as-a-Service platform' }
];

export const AboutPage = () => {
  return (
    <main className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="about-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              About Us
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Engineering the Future of{' '}
              <span className="gradient-text">Electrification</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Skytec EV exists to engineer intelligent charging systems that accelerate the nation's transition to electric mobility.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 md:py-28 bg-white" data-testid="who-we-are">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SectionHeader
                overline="Who We Are"
                title="Technology-Driven Infrastructure Engineering"
              />
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Skytec EV is a technology-driven engineering firm dedicated to building smarter, faster, and more resilient EV charging infrastructure. We combine deep engineering expertise with next-generation technology to build charging infrastructure that is safer, smarter, and more reliable.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                From early-stage analysis to full-scale construction and ongoing system operations, we provide complete, end-to-end electrification solutions for fleets, commercial properties, and municipalities across the nation.
              </p>

              {/* Skytec Company Badge */}
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-slate-50 rounded-xl border border-slate-200">
                <Building2 className="w-8 h-8 text-[#00A2B5]" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">A SKYTEC COMPANY</p>
                  <p className="text-sm text-slate-700">Backed by decades of infrastructure experience</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={TEAM_IMAGE} 
                  alt="Skytec EV Team"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-slate-900">15+</p>
                    <p className="text-sm text-slate-500">Years of Experience</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="mission-vision">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl p-10 border border-slate-200"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mb-6 shadow-lg">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-black text-slate-900 mb-4">Our Mission</h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                To design and deploy scalable, future-ready EV charging infrastructure that enables businesses and fleets to Lead the Charge toward full electrification.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl p-10 border border-slate-200"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mb-6 shadow-lg">
                <Lightbulb className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-black text-slate-900 mb-4">Our Vision</h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                A nation powered by resilient, accessible, and intelligent EV charging ecosystems that support sustainable transportation for all.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20 md:py-28 bg-white" data-testid="philosophy">
        <div className="container-custom">
          <SectionHeader
            overline="Our Philosophy"
            title="What Drives Us"
            centered
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {philosophy.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A2B5]/10 to-[#36D890]/10 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#00A2B5]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-[#00A2B5] to-[#36D890]" data-testid="stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-black text-white mb-2">{stat.value}</p>
                <p className="text-white/80 text-sm font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="timeline">
        <div className="container-custom">
          <SectionHeader
            overline="Our Journey"
            title="Company Milestones"
            centered
          />

          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 mb-8 last:mb-0"
              >
                <div className="flex-shrink-0 w-20 pt-1">
                  <span className="text-xl font-black gradient-text">{milestone.year}</span>
                </div>
                <div className="relative flex-1 pb-8 border-l-2 border-slate-200 pl-6">
                  <div className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-gradient-to-br from-[#00A2B5] to-[#36D890]" />
                  <p className="text-slate-700">{milestone.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner 
        title="Partner With Us"
        subtitle="Join the growing network of businesses electrifying with Skytec EV."
      />
    </main>
  );
};

export default AboutPage;
